// Markdown rendering for assistant replies.
//
// react-markdown rather than the regex-and-dangerouslySetInnerHTML approach in
// FroyoAI.js (fmtAnalysis). That pattern would be an HTML injection path here:
// this renders model output and tool results containing client data, and
// react-markdown does not parse raw HTML unless explicitly told to.

import { useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { BRAND } from "../../config/constants";
import Icon from "../ui/Icon";

/** A copy button that confirms itself for a moment, then goes quiet. */
export function CopyButton({ text, t, label = "Copy", compact = false }) {
  const [copied, setCopied] = useState(false);

  const copy = async (e) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      // Clipboard access can be refused; a textarea fallback still works.
      const ta = document.createElement("textarea");
      ta.value = text;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      try {
        document.execCommand("copy");
      } catch {
        /* nothing more to try */
      }
      document.body.removeChild(ta);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1400);
  };

  return (
    <button
      onClick={copy}
      title={copied ? "Copied" : label}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 4,
        padding: compact ? "3px 6px" : "4px 9px",
        borderRadius: 6,
        background: "transparent",
        border: `1px solid ${copied ? BRAND.green + "60" : t.border}`,
        color: copied ? BRAND.green : t.textMuted,
        fontSize: 10.5,
        fontWeight: 600,
        cursor: "pointer",
        fontFamily: "'Outfit',sans-serif",
        transition: "all 0.12s",
      }}
    >
      <Icon name={copied ? "check" : "clipboard"} size={11} />
      {!compact && (copied ? "Copied" : label)}
    </button>
  );
}

const MONO = "ui-monospace,SFMono-Regular,Menlo,Consolas,'Liberation Mono',monospace";

export default function Markdown({ children, t }) {
  const text = typeof children === "string" ? children : "";

  return (
    <div style={{ fontSize: 13.5, lineHeight: 1.65, color: t.text, overflowWrap: "anywhere" }}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          p: ({ children }) => <p style={{ margin: "0 0 10px" }}>{children}</p>,
          h1: ({ children }) => <Heading t={t} size={17}>{children}</Heading>,
          h2: ({ children }) => <Heading t={t} size={15.5}>{children}</Heading>,
          h3: ({ children }) => <Heading t={t} size={14}>{children}</Heading>,
          h4: ({ children }) => <Heading t={t} size={13.5}>{children}</Heading>,

          ul: ({ children }) => (
            <ul style={{ margin: "0 0 10px", paddingLeft: 20 }}>{children}</ul>
          ),
          ol: ({ children }) => (
            <ol style={{ margin: "0 0 10px", paddingLeft: 20 }}>{children}</ol>
          ),
          li: ({ children }) => <li style={{ margin: "3px 0" }}>{children}</li>,

          strong: ({ children }) => (
            <strong style={{ fontWeight: 700, color: t.text }}>{children}</strong>
          ),
          a: ({ href, children }) => (
            // Model output can contain links; noreferrer keeps the CRM's URL out
            // of whatever is on the other end.
            <a
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: BRAND.blue, textDecoration: "underline" }}
            >
              {children}
            </a>
          ),

          blockquote: ({ children }) => (
            <blockquote
              style={{
                margin: "0 0 10px",
                padding: "2px 0 2px 12px",
                borderLeft: `3px solid ${t.border}`,
                color: t.textSub,
              }}
            >
              {children}
            </blockquote>
          ),

          hr: () => (
            <hr style={{ border: "none", borderTop: `1px solid ${t.border}`, margin: "14px 0" }} />
          ),

          code: ({ inline, className, children }) => {
            const body = String(children).replace(/\n$/, "");

            // react-markdown only sets `inline` for single-backtick spans; a
            // fenced block without a language has neither, hence the newline
            // check as a second signal.
            if (inline || (!className && !body.includes("\n"))) {
              return (
                <code
                  style={{
                    fontFamily: MONO,
                    fontSize: "0.88em",
                    background: t.inputBg,
                    border: `1px solid ${t.border}`,
                    borderRadius: 4,
                    padding: "1px 5px",
                  }}
                >
                  {body}
                </code>
              );
            }

            const language = /language-(\w+)/.exec(className || "")?.[1];
            return <CodeBlock body={body} language={language} t={t} />;
          },
          pre: ({ children }) => <>{children}</>,

          table: ({ children }) => (
            <div style={{ overflowX: "auto", margin: "0 0 12px" }}>
              <table
                style={{
                  borderCollapse: "collapse",
                  fontSize: 12.5,
                  width: "100%",
                  minWidth: 320,
                }}
              >
                {children}
              </table>
            </div>
          ),
          thead: ({ children }) => (
            <thead style={{ background: t.surfaceAlt }}>{children}</thead>
          ),
          th: ({ children }) => (
            <th
              style={{
                textAlign: "left",
                padding: "7px 10px",
                border: `1px solid ${t.border}`,
                fontWeight: 700,
                color: t.textSub,
                fontSize: 11,
                textTransform: "uppercase",
                letterSpacing: "0.04em",
                whiteSpace: "nowrap",
              }}
            >
              {children}
            </th>
          ),
          td: ({ children }) => (
            <td style={{ padding: "7px 10px", border: `1px solid ${t.border}` }}>{children}</td>
          ),
        }}
      >
        {text}
      </ReactMarkdown>
    </div>
  );
}

function Heading({ children, t, size }) {
  return (
    <div style={{ fontSize: size, fontWeight: 700, color: t.text, margin: "16px 0 7px" }}>
      {children}
    </div>
  );
}

function CodeBlock({ body, language, t }) {
  return (
    <div
      style={{
        position: "relative",
        margin: "0 0 12px",
        background: t.inputBg,
        border: `1px solid ${t.border}`,
        borderRadius: 8,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "5px 8px 5px 12px",
          borderBottom: `1px solid ${t.border}`,
        }}
      >
        <span
          style={{
            fontSize: 10,
            fontWeight: 700,
            color: t.textMuted,
            textTransform: "uppercase",
            letterSpacing: "0.07em",
          }}
        >
          {language || "text"}
        </span>
        <CopyButton text={body} t={t} compact />
      </div>
      <pre
        style={{
          margin: 0,
          padding: "10px 12px",
          overflowX: "auto",
          fontFamily: MONO,
          fontSize: 12,
          lineHeight: 1.55,
          color: t.text,
        }}
      >
        <code>{body}</code>
      </pre>
    </div>
  );
}
